/**
 * Demo data for the Dynamic Preference Card module.
 * Pre-loaded orthopedic bundles: THA (27130), TKA (27447), CTR (64721).
 */

import type { SupplyItem, PreferenceCard } from './orSyncEngine';

// ═══════════════════════════════════════════════════
// CPT 27130 — Total Hip Arthroplasty
// ═══════════════════════════════════════════════════
export const THA_PREFERENCE_CARD: PreferenceCard = {
  id: 'pc-tha-001',
  surgeonId: 'surgeon-larkin',
  surgeonName: 'Dr. John J. Larkin',
  cptCode: '27130',
  procedureName: 'Total Hip Arthroplasty',
  lastUpdated: '2025-11-15T08:00:00Z',
  items: [
    { id: 'tha-c1', name: 'Tapered Stem', category: 'implant', size: 'Size 5', quantity: 1, catalogNumber: 'SM-TAP-05' },
    { id: 'tha-c2', name: 'Acetabular Cup', category: 'implant', size: '54mm', quantity: 1, catalogNumber: 'AC-CUP-54' },
    { id: 'tha-c3', name: 'Ceramic Femoral Head', category: 'implant', size: '32mm', quantity: 1, catalogNumber: 'FH-32-CER' },
    { id: 'tha-c4', name: 'Polyethylene Liner', category: 'implant', size: '54mm', quantity: 1, catalogNumber: 'PL-54-XL' },
    { id: 'tha-c5', name: 'Acetabular Screws', category: 'implant', size: '25mm', quantity: 2, catalogNumber: 'AS-TI-25' },
    { id: 'tha-c6', name: 'Simplex Bone Cement', category: 'cement', quantity: 2, catalogNumber: 'SX-BC-40G' },
    { id: 'tha-c7', name: 'Cement Restrictor', category: 'consumable', size: '14mm', quantity: 1, catalogNumber: 'CR-14' },
    { id: 'tha-c8', name: 'Pulse Lavage System', category: 'instrument', quantity: 1, catalogNumber: 'PLS-3000' },
    { id: 'tha-c9', name: 'Charnley Retractor', category: 'instrument', quantity: 1, catalogNumber: 'CHR-STD' },
    { id: 'tha-c10', name: 'Hohmann Retractors', category: 'instrument', quantity: 3, catalogNumber: 'HOH-SET' },
    { id: 'tha-c11', name: 'Closed Suction Drain', category: 'consumable', quantity: 1, catalogNumber: 'HV-100' },
    { id: 'tha-c12', name: 'Trial Components Set', category: 'instrument', quantity: 1, catalogNumber: 'TCS-HIP-UNI' },
    { id: 'tha-c13', name: 'Electrocautery Unit', category: 'instrument', quantity: 1, catalogNumber: 'BOV-FT' },
    { id: 'tha-c14', name: 'Vicryl', category: 'suture', size: '2-0', quantity: 6, catalogNumber: 'J392H' },
    { id: 'tha-c15', name: 'Monocryl', category: 'suture', size: '3-0', quantity: 4, catalogNumber: 'Y426H' },
    { id: 'tha-c16', name: 'Autograft Bone Chips', category: 'graft', quantity: 1, catalogNumber: 'ABG-10CC' },
  ],
};

export const THA_DICTATION_TRANSCRIPT = `Posterior approach total hip arthroplasty. After standard posterior approach and dislocation of the femoral head, acetabulum was reamed to 54mm. A 54mm acetabular cup was impacted and secured with two screws. Trialing was performed using trial components. Final implant: Size 5 tapered stem press-fit into the femoral canal. 32mm ceramic femoral head placed. Polyethylene liner seated and locked. Simplex cement was used for femoral fixation. Cement restrictor placed at 14mm. Pulse lavage used for thorough irrigation. Charnley retractor and Hohmann retractors used for exposure. Hemostasis achieved with bovie cautery. A Hemovac drain was placed. Closure in layers using 2-0 Vicryl for deep fascia, 3-0 Monocryl subcuticular. Bone graft was harvested from the femoral head as autograft. Additionally, a 4-0 Vicryl was used for capsular repair. Estimated blood loss 250cc.`;

export const THA_EXTRACTED_ITEMS: SupplyItem[] = [
  { id: 'tha-d1', name: 'Tapered Stem', category: 'implant', size: 'Size 5', quantity: 1 },
  { id: 'tha-d2', name: 'Acetabular Cup', category: 'implant', size: '54mm', quantity: 1 },
  { id: 'tha-d3', name: 'Ceramic Femoral Head', category: 'implant', size: '32mm', quantity: 1 },
  { id: 'tha-d4', name: 'Polyethylene Liner', category: 'implant', quantity: 1 },
  { id: 'tha-d5', name: 'Acetabular Screws', category: 'implant', quantity: 2 },
  { id: 'tha-d6', name: 'Simplex Bone Cement', category: 'cement', quantity: 1 },
  { id: 'tha-d7', name: 'Cement Restrictor', category: 'consumable', size: '14mm', quantity: 1 },
  { id: 'tha-d8', name: 'Pulse Lavage System', category: 'instrument', quantity: 1 },
  { id: 'tha-d9', name: 'Charnley Retractor', category: 'instrument', quantity: 1 },
  { id: 'tha-d10', name: 'Hohmann Retractors', category: 'instrument', quantity: 3 },
  { id: 'tha-d11', name: 'Closed Suction Drain', category: 'consumable', quantity: 1 },
  { id: 'tha-d12', name: 'Trial Components Set', category: 'instrument', quantity: 1 },
  { id: 'tha-d13', name: 'Electrocautery Unit', category: 'instrument', quantity: 1 },
  { id: 'tha-d14', name: 'Vicryl', category: 'suture', size: '2-0', quantity: 1 },
  { id: 'tha-d15', name: 'Monocryl', category: 'suture', size: '3-0', quantity: 1 },
  { id: 'tha-d16', name: 'autograft', category: 'graft', quantity: 1 },
  // Ad-hoc: capsular repair suture not on standard card
  { id: 'tha-d17', name: 'Vicryl', category: 'suture', size: '4-0', quantity: 1 },
];

// ═══════════════════════════════════════════════════
// CPT 27447 — Total Knee Arthroplasty
// ═══════════════════════════════════════════════════
export const TKA_PREFERENCE_CARD: PreferenceCard = {
  id: 'pc-tka-001',
  surgeonId: 'surgeon-larkin',
  surgeonName: 'Dr. John J. Larkin',
  cptCode: '27447',
  procedureName: 'Total Knee Arthroplasty',
  lastUpdated: '2025-12-01T08:00:00Z',
  items: [
    { id: 'tka-c1', name: 'Femoral Component', category: 'implant', size: 'Size 5', quantity: 1, catalogNumber: 'FC-CR-05' },
    { id: 'tka-c2', name: 'Tibial Baseplate', category: 'implant', size: 'Size 4', quantity: 1, catalogNumber: 'TB-CR-04' },
    { id: 'tka-c3', name: 'Polyethylene Insert', category: 'implant', size: '10mm', quantity: 1, catalogNumber: 'PE-CR-10' },
    { id: 'tka-c4', name: 'Patellar Component', category: 'implant', size: '32mm', quantity: 1, catalogNumber: 'PAT-32-PE' },
    { id: 'tka-c5', name: 'Simplex Bone Cement', category: 'cement', quantity: 3, catalogNumber: 'SX-BC-40G' },
    { id: 'tka-c6', name: 'Cement Restrictor', category: 'consumable', size: '12mm', quantity: 1, catalogNumber: 'CR-12' },
    { id: 'tka-c7', name: 'Pulse Lavage System', category: 'instrument', quantity: 1, catalogNumber: 'PLS-3000' },
    { id: 'tka-c8', name: 'Knee Cutting Guide', category: 'instrument', quantity: 1, catalogNumber: 'KCG-STD' },
    { id: 'tka-c9', name: 'Tibial Alignment Tower', category: 'instrument', quantity: 1, catalogNumber: 'TAT-EXT' },
    { id: 'tka-c10', name: 'Z-Retractors', category: 'instrument', quantity: 2, catalogNumber: 'ZR-KNEE' },
    { id: 'tka-c11', name: 'Electrocautery Unit', category: 'instrument', quantity: 1, catalogNumber: 'BOV-FT' },
    { id: 'tka-c12', name: 'Closed Suction Drain', category: 'consumable', quantity: 1, catalogNumber: 'HV-100' },
    { id: 'tka-c13', name: 'Trial Components Set', category: 'instrument', quantity: 1, catalogNumber: 'TCS-KNEE-UNI' },
    { id: 'tka-c14', name: 'Vicryl', category: 'suture', size: '1-0', quantity: 4, catalogNumber: 'J371H' },
    { id: 'tka-c15', name: 'Vicryl', category: 'suture', size: '2-0', quantity: 6, catalogNumber: 'J392H' },
    { id: 'tka-c16', name: 'Monocryl', category: 'suture', size: '3-0', quantity: 4, catalogNumber: 'Y426H' },
    { id: 'tka-c17', name: 'Skin Staples', category: 'consumable', quantity: 1, catalogNumber: 'SS-35W' },
  ],
};

export const TKA_DICTATION_TRANSCRIPT = `Midline incision, medial parapatellar arthrotomy for total knee arthroplasty. Distal femoral cut made using intramedullary alignment with the knee cutting guide. Tibial alignment tower used for proximal tibial resection. Patella resurfaced with 32mm patellar component cemented in place. Trial components confirmed alignment and stability. Final implants: Size 5 femoral component, Size 4 tibial baseplate, 10mm polyethylene insert, all cemented with Simplex bone cement. Pulse lavage irrigation of all surfaces prior to cementation. Z-retractors used for exposure. Hemostasis with electrocautery. Hemovac drain placed. Closure: 1-0 Vicryl for deep capsule, 2-0 Vicryl for subcutaneous, skin closed with staples. Tourniquet time 62 minutes.`;

export const TKA_EXTRACTED_ITEMS: SupplyItem[] = [
  { id: 'tka-d1', name: 'Femoral Component', category: 'implant', size: 'Size 5', quantity: 1 },
  { id: 'tka-d2', name: 'Tibial Baseplate', category: 'implant', size: 'Size 4', quantity: 1 },
  { id: 'tka-d3', name: 'Polyethylene Insert', category: 'implant', size: '10mm', quantity: 1 },
  { id: 'tka-d4', name: 'Patellar Component', category: 'implant', size: '32mm', quantity: 1 },
  { id: 'tka-d5', name: 'Simplex Bone Cement', category: 'cement', quantity: 1 },
  { id: 'tka-d6', name: 'Pulse Lavage System', category: 'instrument', quantity: 1 },
  { id: 'tka-d7', name: 'Knee Cutting Guide', category: 'instrument', quantity: 1 },
  { id: 'tka-d8', name: 'Tibial Alignment Tower', category: 'instrument', quantity: 1 },
  { id: 'tka-d9', name: 'Z-Retractors', category: 'instrument', quantity: 2 },
  { id: 'tka-d10', name: 'Electrocautery Unit', category: 'instrument', quantity: 1 },
  { id: 'tka-d11', name: 'Closed Suction Drain', category: 'consumable', quantity: 1 },
  { id: 'tka-d12', name: 'Trial Components Set', category: 'instrument', quantity: 1 },
  { id: 'tka-d13', name: 'Vicryl', category: 'suture', size: '1-0', quantity: 1 },
  { id: 'tka-d14', name: 'Vicryl', category: 'suture', size: '2-0', quantity: 1 },
  { id: 'tka-d15', name: 'Skin Staples', category: 'consumable', quantity: 1 },
  // Monocryl on card but NOT used — waste
  // Cement restrictor on card but NOT used — waste
];

// ═══════════════════════════════════════════════════
// CPT 64721 — Carpal Tunnel Release
// ═══════════════════════════════════════════════════
export const CTR_PREFERENCE_CARD: PreferenceCard = {
  id: 'pc-ctr-001',
  surgeonId: 'surgeon-larkin',
  surgeonName: 'Dr. John J. Larkin',
  cptCode: '64721',
  procedureName: 'Carpal Tunnel Release',
  lastUpdated: '2025-10-20T08:00:00Z',
  items: [
    { id: 'ctr-c1', name: 'Lidocaine 1% with Epinephrine', category: 'consumable', size: '20mL', quantity: 1, catalogNumber: 'LID-1E-20' },
    { id: 'ctr-c2', name: 'Bupivacaine 0.25%', category: 'consumable', size: '10mL', quantity: 1, catalogNumber: 'BUP-025-10' },
    { id: 'ctr-c3', name: 'Mini-Retractor (Senn)', category: 'instrument', quantity: 2, catalogNumber: 'SENN-MINI' },
    { id: 'ctr-c4', name: 'Mini-Incision Blade Set', category: 'instrument', quantity: 1, catalogNumber: 'MIB-15C' },
    { id: 'ctr-c5', name: 'Tenotomy Scissors', category: 'instrument', quantity: 1, catalogNumber: 'TS-METZEN' },
    { id: 'ctr-c6', name: 'Freer Elevator', category: 'instrument', quantity: 1, catalogNumber: 'FE-STD' },
    { id: 'ctr-c7', name: 'Bipolar Cautery', category: 'instrument', quantity: 1, catalogNumber: 'BC-MICRO' },
    { id: 'ctr-c8', name: 'Penrose Drain', category: 'consumable', size: '1/4"', quantity: 1, catalogNumber: 'PD-025' },
    { id: 'ctr-c9', name: 'Vicryl', category: 'suture', size: '4-0', quantity: 3, catalogNumber: 'J494H' },
    { id: 'ctr-c10', name: 'Nylon', category: 'suture', size: '4-0', quantity: 4, catalogNumber: 'NY-40-ETH' },
    { id: 'ctr-c11', name: 'Loupe Magnification (3.5x)', category: 'instrument', quantity: 1, catalogNumber: 'LM-35X' },
    { id: 'ctr-c12', name: 'Tourniquet (Forearm)', category: 'instrument', quantity: 1, catalogNumber: 'TQ-FA-STD' },
  ],
};

export const CTR_DICTATION_TRANSCRIPT = `Open carpal tunnel release, left wrist. Wide-awake local anesthesia technique with 20mL of 1% lidocaine with epinephrine and 10mL of 0.25% bupivacaine. Forearm tourniquet applied. Mini-incision at thenar crease, approximately 2cm. Senn retractors placed for exposure. Transverse carpal ligament identified and divided under direct visualization using tenotomy scissors. Freer elevator used to ensure complete release distally and proximally. Median nerve inspected — no signs of neuroma or significant synovitis. Bipolar cautery for hemostasis. Wound irrigated. Closure: 4-0 Nylon interrupted horizontal mattress sutures. Soft dressing applied. Tourniquet time 8 minutes. Patient tolerated well. Additionally, 5-0 Monocryl was used for deep dermal closure.`;

export const CTR_EXTRACTED_ITEMS: SupplyItem[] = [
  { id: 'ctr-d1', name: 'Lidocaine 1% with Epinephrine', category: 'consumable', size: '20mL', quantity: 1 },
  { id: 'ctr-d2', name: 'Bupivacaine 0.25%', category: 'consumable', size: '10mL', quantity: 1 },
  { id: 'ctr-d3', name: 'Mini-Retractor (Senn)', category: 'instrument', quantity: 2 },
  { id: 'ctr-d4', name: 'Tenotomy Scissors', category: 'instrument', quantity: 1 },
  { id: 'ctr-d5', name: 'Freer Elevator', category: 'instrument', quantity: 1 },
  { id: 'ctr-d6', name: 'Bipolar Cautery', category: 'instrument', quantity: 1 },
  { id: 'ctr-d7', name: 'Nylon', category: 'suture', size: '4-0', quantity: 1 },
  { id: 'ctr-d8', name: 'Tourniquet (Forearm)', category: 'instrument', quantity: 1 },
  // Ad-hoc: 5-0 Monocryl for deep dermal not on standard card
  { id: 'ctr-d9', name: 'Monocryl', category: 'suture', size: '5-0', quantity: 1 },
  // Missing from dictation: Mini-Incision Blade Set (opened but not mentioned), Penrose Drain (not used),
  //   Vicryl 4-0 (replaced by Nylon), Loupe Magnification (used but not dictated as supply)
];

// ═══════════════════════════════════════════════════
// Bundle Registry (for procedure selector)
// ═══════════════════════════════════════════════════
export interface DemoBundle {
  card: PreferenceCard;
  transcript: string;
  extractedItems: SupplyItem[];
}

export const DEMO_BUNDLES: Record<string, DemoBundle> = {
  '27130': { card: THA_PREFERENCE_CARD, transcript: THA_DICTATION_TRANSCRIPT, extractedItems: THA_EXTRACTED_ITEMS },
  '27447': { card: TKA_PREFERENCE_CARD, transcript: TKA_DICTATION_TRANSCRIPT, extractedItems: TKA_EXTRACTED_ITEMS },
  '64721': { card: CTR_PREFERENCE_CARD, transcript: CTR_DICTATION_TRANSCRIPT, extractedItems: CTR_EXTRACTED_ITEMS },
};

export const BUNDLE_OPTIONS = [
  { cpt: '27130', label: 'Total Hip Arthroplasty' },
  { cpt: '27447', label: 'Total Knee Arthroplasty' },
  { cpt: '64721', label: 'Carpal Tunnel Release' },
];

// Legacy exports for backward compatibility
export const DEMO_PREFERENCE_CARD = THA_PREFERENCE_CARD;
export const DEMO_DICTATION_TRANSCRIPT = THA_DICTATION_TRANSCRIPT;
export const DEMO_EXTRACTED_ITEMS = THA_EXTRACTED_ITEMS;
